<template>
  <BittsLoading :is-loading="!ready">
    <div class="seat-requests-table">
      <BittsEmptyState
        v-if="showNoResults"
        :font-awesome-icon="['fak', 'search']"
        :font-awesome-icon-style="{ height: '26px', width: '26px' }"
        font-awesome-icon-class="seat-requests-table__empty-state-icon"
        title="No results"
        description="No seat requests match your search"
        class="seat-requests-table__empty-state fade-in"
        data-testid="seat-requests-table-empty-state"
      />
      <BittsTable
        v-else
        class="seat-requests-table__table fade-in"
        :columns="columns"
        :rows="rows"
        :hide-pagination="rows.length <= PAGE_SIZE"
        :pagination-page-size="PAGE_SIZE"
        :enable-cell-text-selection="true"
        :row-selection="{
          mode: 'multiRow',
          enableClickSelection: false,
          checkboxes: false,
          headerCheckbox: false,
        }"
        :passed-context="passedContext"
        row-id="id"
        :compress-columns="true"
        :row-height="64"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsEmptyState, BittsLoading, BittsTable } from '@crossbeam/bitts';

import { ColDef } from '@ag-grid-community/core';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import SeatRequestTableActionsCell from '@/components/team/seat-requests/SeatRequestTableActionsCell.vue';
import SeatRequestTableDateCell from '@/components/team/seat-requests/SeatRequestTableDateCell.vue';
import SeatRequestTableMessageCell from '@/components/team/seat-requests/SeatRequestTableMessageCell.vue';
import SeatRequestTableTypeCell from '@/components/team/seat-requests/SeatRequestTableTypeCell.vue';
import SeatRequestTableUserCell from '@/components/team/seat-requests/SeatRequestTableUserCell.vue';

import useAuth from '@/composables/useAuth';
import { useSeatRequestsStore } from '@/stores';
import { SeatRequest } from '@/types/seat_requests';
import { shallowObjectSearch, sortByKey } from '@/utils';

const {
  search = '',
  seatTypes = [],
  receivedRange = [],
  selectedIds = new Set(),
  isHeaderChecked = false,
  isHeaderIndeterminate = false,
} = defineProps<{
  search?: string;
  seatTypes?: string[];
  receivedRange?: [string, string] | [];
  selectedIds?: Set<string>;
  isHeaderChecked?: boolean;
  isHeaderIndeterminate?: boolean;
}>();

const emit = defineEmits<{
  pick: [id: string];
  pickAll: [checked: boolean];
}>();

const seatRequestsStore = useSeatRequestsStore();
const { ready, fullAccessRequests, salesAccessRequests } =
  storeToRefs(seatRequestsStore);

/* Search */
const fullAccessRequestsSearched = computed(() =>
  fullAccessRequests.value.filter((request) =>
    shallowObjectSearch(request, ['email', 'first_name', 'last_name'], search),
  ),
);
const salesAccessRequestsSearched = computed(() =>
  salesAccessRequests.value.filter((request) =>
    shallowObjectSearch(request, ['email', 'first_name', 'last_name'], search),
  ),
);

/* Table */
const PAGE_SIZE = 10;
function isWithinReceivedRange(request: SeatRequest) {
  const [start, end] = receivedRange;
  if (!start || !end) return true;
  const created = DateTime.fromISO(request.created_at);
  return (
    created >= DateTime.fromISO(start).startOf('day') &&
    created <= DateTime.fromISO(end).endOf('day')
  );
}
const rows = computed(() => {
  const allRequests = [
    ...fullAccessRequestsSearched.value,
    ...salesAccessRequestsSearched.value,
  ];
  return allRequests.filter((request) => {
    const matchesType =
      !seatTypes.length || (!!request.type && seatTypes.includes(request.type));
    return matchesType && isWithinReceivedRange(request);
  });
});

const hasActiveFilters = computed(
  () => !!seatTypes.length || receivedRange.length === 2,
);
const showNoResults = computed(
  () => (search !== '' || hasActiveFilters.value) && !rows.value.length,
);

/* Selection */
const selectableIds = computed(() => rows.value.map((request) => request.id));

const { hasScope, userManagementEnabledForOrg } = useAuth();
const canManageRequests = computed(
  () => hasScope('write:members') && userManagementEnabledForOrg.value,
);

const passedContext = {
  isRequestSelected(id: string) {
    return selectedIds.has(id);
  },
  handlePickRequest(seatRequest: SeatRequest) {
    emit('pick', seatRequest.id);
  },
};

const columns = computed<ColDef[]>(() => [
  {
    field: 'first_name',
    cellRenderer: SeatRequestTableUserCell,
    suppressMovable: true,
    sortable: true,
    width: 300,
    headerComponentParams: {
      displayName: 'Name',
      showArrow: true,
      showCheckbox: canManageRequests.value,
      getChecked: () => isHeaderChecked,
      getIndeterminate: () => isHeaderIndeterminate,
      onChecked: (checked: boolean) => emit('pickAll', checked),
    },
    comparator: (_, __, { data: a }, { data: b }) => {
      return a.first_name.localeCompare(b.first_name);
    },
  },
  {
    field: 'type',
    cellRenderer: SeatRequestTableTypeCell,
    suppressMovable: true,
    sortable: true,
    headerComponentParams: { displayName: 'Seat Type', showArrow: true },
    comparator: (_, __, { data: a }, { data: b }) => {
      return a.type.localeCompare(b.type);
    },
  },
  {
    field: 'message',
    cellRenderer: SeatRequestTableMessageCell,
    width: 300,
    suppressMovable: true,
    headerComponentParams: { displayName: 'Message' },
  },
  {
    field: 'created_at',
    cellRenderer: SeatRequestTableDateCell,
    suppressMovable: true,
    sort: 'asc',
    sortable: true,
    headerComponentParams: { displayName: 'Received' },
    comparator: (_, __, { data: a }, { data: b }) => {
      return sortByKey('created_at', 'desc', 'raw')(a, b);
    },
  },
  {
    field: '',
    cellRenderer: SeatRequestTableActionsCell,
    suppressMovable: true,
  },
]);

defineExpose({ rows, selectableIds });
</script>
<style lang="pcss" scoped>
@reference "../../../css/theme.css";
.seat-requests-table__header-section {
  @apply flex justify-between items-center mb-16;
}
.seat-requests-table__empty-state {
  @apply py-40;
}
</style>

<style lang="pcss">
@reference "../../../css/theme.css";
.seat-requests-table__table {
  .ag-row .ag-cell:last-of-type {
    @apply justify-end;
  }
}
.seat-requests-table__empty-state-icon {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
</style>
